import { useMemo, useState } from "react";
import Fuse from "fuse.js";
import Select from "react-select";
import { Form, Formik } from "formik";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import { PropagateLoader } from "react-spinners";
import Button from "../../atoms/Button";
import InputField from "../../atoms/InputField";
import { UPDATE_CASO } from "../../../api/apiCasos";
import { UPLOAD_FILE } from "../../../api/apiFiles";
import { UPDATE_JUICIO } from "../../../api/apiJuicio";
import { UPDATE_RECURSO } from  "../../../api/apiRecurso";
import { UPDATE_FISCALIZACION } from "../../../api/apiFiscalizacion";
import { UPDATE_CONTRACTUAL_DOCS } from "../../../api/apiContractualDocs";
import { getEtapa } from "../../../utils/getEtapa";
import { allowFiles } from "../../../data/allowFiles";
import { useSession } from "../../../context/SessionContext";

const CONTRACTUAL_FIELDS = {
  contract: "service_contracts",
  minutas: "minutes",
  cotizacion: "quotation",
};

const HANDLERS_BY_ETAPA = {
  juicio: UPDATE_JUICIO,
  fiscalizacion: UPDATE_FISCALIZACION,
  recurso: UPDATE_RECURSO,
  contract: UPDATE_CONTRACTUAL_DOCS,
  minutas: UPDATE_CONTRACTUAL_DOCS,
  cotizacion: UPDATE_CONTRACTUAL_DOCS,
};

const DFUploadDocs = ({ onClose, refetch, etapa, etapaId, lastData }) => {
  const { id, caso_id } = useParams();
  const [searchTerm, setSearchTerm] = useState("");
  const { user } = useSession();

  const normalizePrevFiles = (input) => {
    if (!input) return [];
    if (Array.isArray(input)) return input;
    if (Array.isArray(input?.files)) return input.files;
    if (input?.rules && (input?.docId || input?.docName)) return [input];
    return [];
  };

  const etapaHandler = HANDLERS_BY_ETAPA[etapa];

  const etapaDocs = useMemo(
    () => allowFiles.filter((d) => d.tableName === etapa),
    [etapa]
  );

  const [loading, setLoading] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState(null);
  const [filesByKey, setFilesByKey] = useState({});

  const selectedDoc = useMemo(
    () => etapaDocs.find((d) => d.id === selectedDocId) || null,
    [etapaDocs, selectedDocId]
  );

  const initialValues = { fecha: "", comentarios: "" };

  const validate = (values) => {
    const errors = {};
    if (!selectedDoc) errors.doc = "Selecciona un documento";
    if (!values.fecha) errors.fecha = "Campo requerido";

    if (selectedDoc?.rules?.length) {
      selectedDoc.rules.forEach((rule, idx) => {
        const uniqueKey = `${rule.typeFile}||${idx}`;
        if (rule.required && (!filesByKey[uniqueKey] || filesByKey[uniqueKey].length === 0)) {
          errors[uniqueKey] = "Archivo requerido";
        }
      });
    }
    return errors;
  };

  const handleFileChange = (e, uniqueKey) => {
    const incoming = Array.from(e.target.files || []);
    setFilesByKey((prev) => ({
      ...prev,
      [uniqueKey]: [...(prev[uniqueKey] || []), ...incoming],
    }));
  };

  const removeFile = (uniqueKey, index) => {
    setFilesByKey((prev) => {
      const arr = [...(prev[uniqueKey] || [])];
      arr.splice(index, 1);
      return { ...prev, [uniqueKey]: arr };
    });
  };

  const onSubmit = async (values) => {
    if (!selectedDoc) return toast.error("Selecciona un documento.");
    if (!etapaHandler) return toast.error(`No hay handler para la etapa "${etapa}".`);

    try {
      setLoading(true);

      const randomKey = crypto?.randomUUID;

      const rulesPayload = await Promise.all(
        (selectedDoc.rules || []).map(async (rule, idx) => {
          const uniqueKey = `${rule.typeFile}||${idx}`;
          const chosen = filesByKey[uniqueKey] || [];
          const s3Keys = [];
          const filenames = [];

          for (const file of chosen) {
            const filename = file.name;
            const mimetype = file.type || "application/octet-stream";
            const folder = `defensa_fiscal/${etapa}/${selectedDoc.id}/`;

            const { url, s3Key } = await UPLOAD_FILE(
              id,
              filename,
              mimetype,
              folder
            );

            await fetch(url, {
              method: "PUT",
              headers: { "Content-Type": mimetype },
              body: file,
            });

            s3Keys.push(s3Key);
            filenames.push(filename);
          }

          return {
            index: idx,
            uniqueKey,
            typeFile: rule.typeFile,
            fileName: rule.fileName,
            name: rule.name,
            required: !!rule.required,
            s3Keys,
            filenames,
          };
        })
      );

      const filesPayload = {
        docId: selectedDoc.id,
        docName: selectedDoc.name,
        fecha: values.fecha,
        comentarios: values.comentarios,
        primaryKey: randomKey,
        rules: rulesPayload,
        user: user?.roleInfo.name || "Desconocido",
      };

      const prevFiles = normalizePrevFiles(lastData);
      const mergedFiles = [...prevFiles, filesPayload];

      const targetField = (etapa === "juicio" || etapa === "fiscalizacion" || etapa === "recurso")
        ? "files"
        : CONTRACTUAL_FIELDS[etapa];

      if (!targetField) {
        toast.error("Etapa inválida: no se pudo determinar el campo a actualizar.");
        return;
      }

      const body = {
        [targetField]: mergedFiles,
        status: selectedDoc?.text,
      };

      await etapaHandler(etapaId, body);

      await UPDATE_CASO(caso_id, {
        status: `${getEtapa(etapa)}: ${selectedDoc.name} ${values.fecha}`,
      });

      toast.success("Documentos subidos y registrados correctamente.");
      refetch?.();
      onClose?.();
    } catch (err) {
      console.error(err);
      toast.error("Error al subir/registrar documentos.");
    } finally {
      setLoading(false);
    }
  };

  const fuse = useMemo(() => {
    return new Fuse(etapaDocs, {
      keys: ["name"],
      threshold: 0.4,
    });
  }, [etapaDocs]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return etapaDocs.map((doc) => ({ value: doc.id, label: doc.name }));
    const results = fuse.search(searchTerm);
    return results.map(r => ({ value: r.item.id, label: r.item.name }));
  }, [searchTerm, fuse, etapaDocs]);

  return (
    <Formik initialValues={initialValues} validate={validate} onSubmit={onSubmit} enableReinitialize>
      {({ errors }) => (
        <Form className="p-4 flex flex-col items-center space-y-6 w-full">
          <div className="w-full">
            <label className="text-sm font-bold mb-1 block">Documento de la etapa</label>
            <Select
              options={filteredOptions}
              value={selectedDocId ? { value: selectedDocId, label: etapaDocs.find(d => d.id === selectedDocId)?.name } : null}
              onChange={(option) => {
                setSelectedDocId(option?.value || null);
                setFilesByKey({});
              }}
              onInputChange={(inputValue) => setSearchTerm(inputValue)}
              placeholder={etapaDocs.length ? "Selecciona un documento" : "No hay documentos para esta etapa"}
              isClearable
            />
            {errors.doc && <p className="text-[#e73842] text-sm mt-1">{errors.doc}</p>}
          </div>
          <div className="w-full col-span-2 grid grid-cols-1 md:grid-cols-2 gap-2">
            <InputField label="Fecha del documento" name="fecha" type="date" />
            <InputField label="Comentarios" name="comentarios" />
          </div>

          {selectedDoc && selectedDoc.rules?.length > 0 && (
            <div className="w-full space-y-4">
              {selectedDoc.rules.map((rule, idx) => {
                const uniqueKey = `${rule.typeFile}||${idx}`;
                const chosen = filesByKey[uniqueKey] || [];
                return (
                  <div
                    key={uniqueKey}
                    className="w-full flex flex-col md:flex-row md:space-x-4 border rounded-md p-4"
                  >
                    <div className="w-full flex flex-col">
                      <label className="text-sm font-bold mb-1">
                        {rule.name}{" "}
                        {rule?.required && <span className="text-red-500">*</span>}
                        <span className="ml-2 text-xs text-gray-500">(.{rule.typeFile})</span>
                      </label>
                      <input
                        type="file"
                        multiple
                        accept={`.${rule.typeFile}`}
                        onChange={(e) => handleFileChange(e, uniqueKey)}
                        className="w-full p-2 border-2 border-black rounded-xl"
                      />

                      {errors[uniqueKey] && (
                        <p className="text-[#e73842] text-sm mt-1">{errors[uniqueKey]}</p>
                      )}

                      {chosen.length > 0 && (
                        <ul className="mt-2 text-sm list-disc list-inside space-y-1">
                          {chosen.map((f, i) => (
                            <li key={`${uniqueKey}-${i}`} className="flex items-center justify-between">
                              <span className="truncate">{f.name}</span>
                              <button
                                type="button"
                                onClick={() => removeFile(uniqueKey, i)}
                                className="text-xs underline ml-2"
                              >
                                Quitar
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <Button
            type="submit"
            text={loading ? <PropagateLoader color="red" /> : "Enviar"}
            bgColor="blue"
            disabled={loading || !selectedDoc}
          />
        </Form>
      )}
    </Formik>
  );
};

export default DFUploadDocs;
